/**
 * Patron account page for the public portal.
 * Shows account summary, loans, and reservations.
 */

import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePatronAuth, portalFetch } from "../contexts/PatronAuthContext";
import { recordTypeIcon } from "@/lib/recordTypes";
import { GdprSection } from "../components";

interface LoanItem {
  id: string;
  checkoutDate: string;
  dueDate: string;
  returnDate: string | null;
  isOverdue: boolean;
  daysOverdue: number;
  renewalCount: number;
  canRenew: boolean;
  item: {
    id: string;
    barcode: string;
    callNumber: string;
  };
  record: {
    id: string;
    title: string;
    subtitle: string | null;
    type: string;
    authors: string | null;
  };
}

interface ReservationItem {
  id: string;
  createdAt: string;
  status: string;
  queuePosition: number;
  expiresAt: string | null;
  notificationSentAt: string | null;
  canCancel: boolean;
  pickupLocation: {
    id: string;
    name: string;
  };
  record: {
    id: string;
    title: string;
    subtitle: string | null;
    type: string;
    authors: string | null;
  };
}

interface AccountSummary {
  status: string;
  statusReason: string | null;
  canBorrow: boolean;
  fineBalance: number;
  hasFines: boolean;
  expirationDate: string | null;
  isExpired: boolean;
  loans: {
    active: number;
    overdue: number;
    limit: number;
    atLimit: boolean;
  };
  reservations: {
    active: number;
    limit: number;
    atLimit: boolean;
  };
}

export function AccountPage() {
  const { patron, isAuthenticated, refreshPatron } = usePatronAuth();
  const queryClient = useQueryClient();

  // Fetch account summary
  const { data: summary, isLoading: summaryLoading } = useQuery<AccountSummary>({
    queryKey: ["portal", "account", "summary"],
    queryFn: async (): Promise<AccountSummary> => {
      const response = await portalFetch("/account/summary");
      if (!response.ok) throw new Error("Failed to fetch summary");
      return (await response.json()) as AccountSummary;
    },
    enabled: isAuthenticated,
  });

  // Fetch active loans
  const { data: loansData, isLoading: loansLoading } = useQuery<{
    loans: LoanItem[];
    total: number;
  }>({
    queryKey: ["portal", "account", "loans"],
    queryFn: async (): Promise<{ loans: LoanItem[]; total: number }> => {
      const response = await portalFetch("/account/loans");
      if (!response.ok) throw new Error("Failed to fetch loans");
      return (await response.json()) as { loans: LoanItem[]; total: number };
    },
    enabled: isAuthenticated,
  });

  // Fetch reservations
  const { data: reservationsData, isLoading: reservationsLoading } = useQuery<{
    reservations: ReservationItem[];
    total: number;
  }>({
    queryKey: ["portal", "account", "reservations"],
    queryFn: async (): Promise<{ reservations: ReservationItem[]; total: number }> => {
      const response = await portalFetch("/account/reservations");
      if (!response.ok) throw new Error("Failed to fetch reservations");
      return (await response.json()) as { reservations: ReservationItem[]; total: number };
    },
    enabled: isAuthenticated,
  });

  // Renew loan mutation
  const renewMutation = useMutation({
    mutationFn: async (loanId: string): Promise<void> => {
      const response = await portalFetch(`/account/loans/${loanId}/renew`, { method: "POST" });
      if (!response.ok) {
        const errorData = (await response.json()) as { error?: string };
        throw new Error(errorData.error || "Échec de la prolongation");
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal", "account", "loans"] });
      void queryClient.invalidateQueries({ queryKey: ["portal", "account", "summary"] });
      void refreshPatron();
    },
  });

  // Cancel reservation mutation
  const cancelMutation = useMutation({
    mutationFn: async (reservationId: string): Promise<void> => {
      const response = await portalFetch(`/account/reservations/${reservationId}/cancel`, {
        method: "POST",
      });
      if (!response.ok) {
        const errorData = (await response.json()) as { error?: string };
        throw new Error(errorData.error || "Échec de l'annulation");
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal", "account", "reservations"] });
      void queryClient.invalidateQueries({ queryKey: ["portal", "account", "summary"] });
      void refreshPatron();
    },
  });

  if (!isAuthenticated || !patron) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          Veuillez vous connecter pour accéder à votre compte.
        </p>
        <Link
          to="/opac/login"
          className="mt-4 inline-block rounded-lg bg-blue-600 px-6 py-3 font-medium text-white hover:bg-blue-700"
        >
          Se connecter
        </Link>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Bonjour, {patron.firstName}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Carte n° {patron.barcode} • {patron.homeLocation.name}
          </p>
        </div>
        <Link
          to="/opac/account/profile"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          Mon profil
        </Link>
      </div>

      {/* Summary cards */}
      {summaryLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : summary ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Loans card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {summary.loans.active}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Prêts en cours (max {summary.loans.limit})
                </p>
              </div>
            </div>
            {summary.loans.overdue > 0 && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                ⚠️ {summary.loans.overdue} en retard
              </p>
            )}
          </div>

          {/* Reservations card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {summary.reservations.active}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Réservations (max {summary.reservations.limit})
                </p>
              </div>
            </div>
          </div>

          {/* Fines card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  summary.hasFines
                    ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                    : "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                }`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {summary.fineBalance.toFixed(2)} €
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {summary.hasFines ? "À régler" : "Pas de retard"}
                </p>
              </div>
            </div>
          </div>

          {/* Status card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  summary.canBorrow
                    ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
                }`}
              >
                {summary.canBorrow ? (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                )}
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {summary.canBorrow ? "Compte actif" : "Emprunts suspendus"}
                </p>
                {summary.expirationDate && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Expire le {formatDate(summary.expirationDate)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Loans section */}
      <section>
        <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          Mes prêts en cours
        </h2>
        {loansLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : loansData && loansData.loans.length > 0 ? (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {loansData.loans.map((loan) => (
                <li key={loan.id} className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xl dark:bg-gray-700">
                      {recordTypeIcon(loan.record.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/opac/record/${loan.record.id}`}
                        className="font-medium text-gray-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400"
                      >
                        {loan.record.title}
                      </Link>
                      {loan.record.authors && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {loan.record.authors}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            loan.isOverdue
                              ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {loan.isOverdue
                            ? `En retard de ${String(loan.daysOverdue)} jour${loan.daysOverdue > 1 ? "s" : ""}`
                            : `À rendre le ${formatDate(loan.dueDate)}`}
                        </span>
                        {loan.renewalCount > 0 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Prolongé {loan.renewalCount}x
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0">
                      {loan.canRenew && (
                        <button
                          onClick={() => {
                            renewMutation.mutate(loan.id);
                          }}
                          disabled={renewMutation.isPending}
                          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {renewMutation.isPending ? "..." : "Prolonger"}
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
            <p className="text-gray-500 dark:text-gray-400">Vous n'avez aucun prêt en cours.</p>
            <Link
              to="/opac/search"
              className="mt-4 inline-block text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Parcourir le catalogue →
            </Link>
          </div>
        )}
      </section>

      {/* Reservations section */}
      <section>
        <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          Mes réservations
        </h2>
        {reservationsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : reservationsData && reservationsData.reservations.length > 0 ? (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {reservationsData.reservations.map((reservation) => (
                <li key={reservation.id} className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xl dark:bg-gray-700">
                      {recordTypeIcon(reservation.record.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/opac/record/${reservation.record.id}`}
                        className="font-medium text-gray-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400"
                      >
                        {reservation.record.title}
                      </Link>
                      {reservation.record.authors && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {reservation.record.authors}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            reservation.status === "ready"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                          }`}
                        >
                          {reservation.status === "ready"
                            ? "Disponible pour retrait"
                            : reservation.status === "to_prepare"
                              ? "En préparation"
                              : `En attente (position ${String(reservation.queuePosition)})`}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Retrait : {reservation.pickupLocation.name}
                        </span>
                        {reservation.expiresAt && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Expire le {formatDate(reservation.expiresAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0">
                      {reservation.canCancel && (
                        <button
                          onClick={() => {
                            if (confirm("Êtes-vous sûr de vouloir annuler cette réservation ?")) {
                              cancelMutation.mutate(reservation.id);
                            }
                          }}
                          disabled={cancelMutation.isPending}
                          className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
                        >
                          {cancelMutation.isPending ? "..." : "Annuler"}
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
            <p className="text-gray-500 dark:text-gray-400">
              Vous n'avez aucune réservation en cours.
            </p>
            <Link
              to="/opac/search"
              className="mt-4 inline-block text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Parcourir le catalogue →
            </Link>
          </div>
        )}
      </section>

      {/* GDPR Section */}
      <GdprSection />
    </div>
  );
}

export default AccountPage;
